import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { QuoteService } from 'src/app/admin-core/apiservices/quote.service';
import { AlertService } from 'src/app/admin-core/services';

/**
 * Merge one split line into another line of the same product on the same order.
 *
 * The admin always picks the destination — there is no implicit "merge into the parent". The list
 * is every line of the same split tree that can still absorb quantity, numbered the same way the
 * order's product table numbers its rows (1, 1.1, 1.2 ...).
 */
@Component({
  standalone: false,
    selector: 'app-revert-split-modal',
    // Eager: Angular 22 defaults every component to OnPush, which would leave this view
    // unpainted after its subscribe callbacks (see app.component.ts for the full note).
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './revert-split-modal.component.html',
    styleUrls: ['./revert-split-modal.component.css'],
})
export class RevertSplitModalComponent implements OnInit {
    @Input() quoteId!: number;
    @Input() productId!: number;
    /** The line being merged away. */
    @Input() childId!: number;
    @Input() orderId!: number;
    @Input() exportId!: string;
    @Input() frontendUserId!: number;
    @Input() uom: string = 'SQM';
    @Input() productName: string = '';

    isLoading = false;
    isMerging = false;

    /** The line being merged, as returned by the server. */
    source: any = null;
    /** Lines it may be merged into. */
    targets: any[] = [];
    selectedTargetId: number | null = null;
    errorMessage: string | null = null;

    /**
     * What the merge would do to consumables, fetched when a target is picked.
     *
     * Consumables are re-sized for the merged quantity, never added together — adhesive is
     * non-linear, so summing over-charges. Where only ONE of the two lines carries a consumable it
     * is applied across the merged quantity, because a line carries it for all of its quantity or
     * none; the admin is told this is happening rather than asked to choose.
     */
    preview: any = null;
    isPreviewLoading = false;
    private previewToken = 0;

    constructor(
        public activeModal: NgbActiveModal,
        private quoteService: QuoteService,
        private alertService: AlertService,
    ) {}

    ngOnInit(): void {
        this.loadTargets();
    }

    loadTargets(): void {
        this.isLoading = true;
        // skipLoader: this dialog drives its own .page-loader off isLoading (see the template), so
        // it must not also push the shared SpinnerService. Nothing renders that service today, so
        // this changes nothing on screen — it keeps the dialog's loading state self-contained, the
        // same as getMergePreview below.
        this.quoteService.getSplitChildren(this.quoteId, this.productId, true).subscribe({
            next: (data: any) => {
                const children = data?.children ?? [];
                const lines = data?.lines ?? [];
                const linesById = new Map<number, any>(lines.map((l: any) => [l.id, l]));

                this.source = children.find((c: any) => c.id === this.childId) ?? null;
                this.targets = (this.source?.mergeTargetIds ?? [])
                    .map((id: number) => linesById.get(id))
                    .filter(Boolean);

                // Keep the admin's pick across a reload if it is still offered.
                const stillValid = this.selectedTargetId != null &&
                    this.targets.some(t => t.id === this.selectedTargetId);
                if (!stillValid) {
                    this.selectedTargetId = null;
                    this.preview = null;
                } else if (this.selectedTargetId != null) {
                    // Quantities may have moved under us — re-price the preview.
                    this.loadPreview(this.selectedTargetId);
                }

                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
                this.alertService.error('Failed to load the lines available to merge into.');
            }
        });
    }

    /**
     * Show each line's stock required date only when the lines actually differ on it.
     *
     * On a split-delivery order every line carries its own SRD, and that is frequently the ONLY
     * thing telling two lines apart — same product, same quantity, same location. Without it the
     * admin cannot tell which line they are merging into. On a normal order every line inherits the
     * order's single date, so showing it would just be noise.
     */
    get showSrd(): boolean {
        const dates = [this.source, ...this.targets].filter(Boolean).map(l => l.srd ?? null);
        return new Set(dates).size > 1;
    }

    get sourceQtyLabel(): string {
        if (!this.source) return '';
        return `${this.source.quantity}${this.unitLabel ? ' ' + this.unitLabel : ''}`;
    }

    get unitLabel(): string {
        return this.uom === 'SQM' ? 'm²' : (this.uom ?? '');
    }

    /** The quantity the chosen destination ends up with. */
    get mergedQty(): number | null {
        const target = this.targets.find(t => t.id === this.selectedTargetId);
        if (!target || !this.source) return null;
        return +(Number(target.quantity) + Number(this.source.quantity)).toFixed(2);
    }

    selectTarget(id: number): void {
        if (this.isMerging) return;
        // The row and its radio both fire this; re-previewing the same target would be a wasted
        // round trip and would flicker the price the admin is reading.
        if (this.selectedTargetId === id) return;
        this.selectedTargetId = id;
        this.loadPreview(id);
    }

    private loadPreview(targetId: number): void {
        this.preview = null;
        this.isPreviewLoading = true;
        // Guard against an out-of-order response when the admin changes target quickly.
        const token = ++this.previewToken;
        this.quoteService.getMergePreview(this.childId, targetId, true).subscribe({
            next: (data: any) => {
                if (token !== this.previewToken) return;
                this.preview = data;
                this.isPreviewLoading = false;
            },
            error: () => {
                if (token !== this.previewToken) return;
                // Non-fatal: the merge itself still works, the admin just does not get the preview.
                this.preview = null;
                this.isPreviewLoading = false;
            }
        });
    }

    get hasConsumableMismatch(): boolean {
        return !!this.preview?.has_mismatch;
    }

    /** "Premium Adhesive" reads better than "LATICRETE_ADHESIVE". */
    consumableLabel(type: string): string {
        if (!type) return '';
        return type
            .toLowerCase()
            .split('_')
            .map(w => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ');
    }

    /** "... are on only one of these two lines" once more than one consumable differs. */
    get mismatchedIsPlural(): boolean {
        return (this.preview?.mismatched_types?.length ?? 0) > 1;
    }

    get mismatchedLabels(): string {
        return (this.preview?.mismatched_types ?? []).map((t: string) => this.consumableLabel(t)).join(', ');
    }

    /** Consumable total for the merged line, always priced across the full merged quantity. */
    get consumableTotal(): number | null {
        if (!this.preview) return null;
        return this.preview.apply?.total_price ?? null;
    }

    toKebabCase(value: any): string {
        if (!value || typeof value !== 'string') return '';
        return value.toLowerCase().replace(/\s+/g, '-');
    }

    merge(): void {
        if (this.selectedTargetId == null || this.isMerging) return;
        this.isMerging = true;
        this.errorMessage = null;

        // Snapshot what is about to move, so the caller can say exactly what happened. Read it now:
        // a reload after this point would replace these rows.
        const outcome = {
            sourceLabel: this.source?.label ?? '',
            targetLabel: this.targets.find(t => t.id === this.selectedTargetId)?.label ?? '',
            movedQty: this.source?.quantity ?? null,
            mergedQty: this.mergedQty,
            unit: this.unitLabel,
        };

        // Always the full merge: a consumable on either line is applied across the merged
        // quantity. Sent explicitly rather than relying on the server's default for a missing flag.
        // skipLoader for the same reason as loadTargets — isMerging already drives the dialog's
        // own loader and disables both buttons, and the static backdrop blocks the page behind.
        this.quoteService.revertSplitQuoteProduct(
            this.childId, this.selectedTargetId, true, true
        ).subscribe({
            next: () => {
                this.isMerging = false;
                this.activeModal.close(outcome);
            },
            error: (err: any) => {
                this.isMerging = false;
                this.errorMessage = err?.error?.message || err?.message || 'Failed to merge.';
                // The tree may have moved under us — reload so the offered lines are current.
                this.loadTargets();
            }
        });
    }
}
